import { SubscriptionListUrlSortField } from "@dashboard/orders/urls";
import { StatusType } from "@dashboard/types";

export interface SubscriptionRow {
  id: string;
  status: string | null;
  interval: string | null;
  amount: {
    value: string | null;
    currency: string | null;
  };
  nextPaymentDate: string | null;
  createdAt: string | null;
  times: number | null;
  timesRemaining: number | null;
  metadata?: {
    productsAmount?: string;
    shippingAmount?: string;
    shippingCurrency?: string;
    totalAmount?: string;
    shippingMethodId?: string;
  };
  saleor: {
    orderId: string | null;
    orderNumber: string | null;
    orderCreated: string | null;
    userEmail: string | null;
  };
}

export function mapSubscriptionStatus(rawStatus: string | null): {
  label: string;
  status: StatusType;
} {
  const normalized = (rawStatus ?? "").toLowerCase();

  switch (normalized) {
    case "active":
      return {
        label: "Active",
        status: StatusType.SUCCESS,
      };
    case "pending":
      return {
        label: "Pending",
        status: StatusType.WARNING,
      };
    case "completed":
      return {
        label: "Completed",
        status: StatusType.INFO,
      };
    case "canceled":
    case "cancelled":
    case "suspended":
    case "paused":
      return {
        label: "Canceled",
        status: StatusType.ERROR,
      };
    default:
      return {
        label: rawStatus ?? "—",
        status: StatusType.INFO,
      };
  }
}

function parseOrderNumber(num: string | null | undefined): number {
  if (!num) {
    return 0;
  }
  const digits = num.replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : 0;
}

function parseNextPaymentDate(value: string | null | undefined): number {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function parseDate(value: string | null | undefined): number {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function parseAmountValue(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : n;
}

export function sortSubscriptionRows(
  items: SubscriptionRow[],
  sort: SubscriptionListUrlSortField,
  asc: boolean,
): SubscriptionRow[] {
  const dir = asc ? 1 : -1;

  const cmp = (a: SubscriptionRow, b: SubscriptionRow): number => {
    switch (sort) {
      case SubscriptionListUrlSortField.order:
        return (
          dir *
          (parseOrderNumber(a.saleor.orderNumber) -
            parseOrderNumber(b.saleor.orderNumber))
        );
      case SubscriptionListUrlSortField.customer:
        return (
          dir *
          String(a.saleor.userEmail ?? "").localeCompare(
            String(b.saleor.userEmail ?? ""),
            undefined,
            { sensitivity: "base" },
          )
        );
      case SubscriptionListUrlSortField.status:
        return (
          dir *
          String(a.status ?? "").localeCompare(
            String(b.status ?? ""),
            undefined,
            {
              sensitivity: "base",
            },
          )
        );
      case SubscriptionListUrlSortField.nextPayment:
        return (
          dir *
          (parseNextPaymentDate(a.nextPaymentDate) -
            parseNextPaymentDate(b.nextPaymentDate))
        );
      case SubscriptionListUrlSortField.interval:
        return (
          dir *
          String(a.interval ?? "").localeCompare(
            String(b.interval ?? ""),
            undefined,
            {
              sensitivity: "base",
            },
          )
        );
      case SubscriptionListUrlSortField.amount:
        return (
          dir *
          (parseAmountValue(a.amount.value) - parseAmountValue(b.amount.value))
        );
      case SubscriptionListUrlSortField.originalOrderDate:
        return (
          dir *
          (parseDate(a.saleor.orderCreated) - parseDate(b.saleor.orderCreated))
        );
      default:
        return 0;
    }
  };

  return [...items].sort(cmp);
}

export function filterSubscriptionRows(
  items: SubscriptionRow[],
  query: string | undefined,
  statuses: string[] | undefined,
): SubscriptionRow[] {
  let out = items;

  if (statuses?.length) {
    const lower = statuses.map(s => s.toLowerCase());
    out = out.filter(item =>
      lower.includes(String(item.status ?? "").toLowerCase()),
    );
  }

  const q = query?.trim().toLowerCase();
  if (q) {
    out = out.filter(item => {
      const haystack = [
        item.id,
        item.saleor.orderNumber,
        item.saleor.orderCreated,
        item.saleor.userEmail,
        item.status,
        item.interval,
        item.nextPaymentDate,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  return out;
}
